import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatCurrency, formatDate } from '../utils/dateUtils.ts';
import {
  getAssets,
  getIncome,
  createIncome,
  updateIncome,
  deleteIncome,
  FIIAsset,
  FIIIncome as FIIIncomeType,
  CreateFIIIncomeRequest,
} from '../services/fiiApi.ts';
import './FIIIncome.css';

function getCurrentCompetence(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const y = now.getFullYear();
  return `${m}/${y}`;
}

function competenceToDateRange(competence: string): { start_date: string; end_date: string } {
  const [month, year] = competence.split('/');
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  const start = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const end = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start_date: start, end_date: end };
}

function buildCompetenceOptions(): string[] {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const y = d.getFullYear();
    opts.push(`${m}/${y}`);
  }
  return opts;
}

const FIIIncome: React.FC = () => {
  const [incomes, setIncomes] = useState<FIIIncomeType[]>([]);
  const [assets, setAssets] = useState<FIIAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingIncome, setEditingIncome] = useState<FIIIncomeType | null>(null);

  const [filterCompetence, setFilterCompetence] = useState(getCurrentCompetence);
  const [filterTicker, setFilterTicker] = useState<number | ''>('');
  const [filterType, setFilterType] = useState<string>('');

  const [form, setForm] = useState<CreateFIIIncomeRequest>({
    fii_asset_id: 0,
    date: '',
    type: 'dividendo',
    total_value: 0,
  });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const competenceOptions = useMemo(() => buildCompetenceOptions(), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ass = await getAssets();
      setAssets(ass);
      const { start_date, end_date } = competenceToDateRange(filterCompetence);
      const params: { start_date: string; end_date: string; fii_asset_id?: number; type?: 'dividendo' | 'jcp' } = {
        start_date,
        end_date,
      };
      if (filterTicker) params.fii_asset_id = filterTicker as number;
      if (filterType) params.type = filterType as 'dividendo' | 'jcp';
      const inc = await getIncome(params);
      setIncomes(Array.isArray(inc) ? (inc as FIIIncomeType[]) : []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar rendimentos');
    } finally {
      setLoading(false);
    }
  }, [filterCompetence, filterTicker, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setWarning('');
    try {
      if (editingIncome) {
        await updateIncome(editingIncome.id, {
          date: form.date,
          type: form.type,
          total_value: form.total_value,
        });
      } else {
        const result: any = await createIncome(form);
        if (result?.warning) setWarning(result.warning);
      }
      setShowForm(false);
      setEditingIncome(null);
      resetForm();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao salvar rendimento');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Confirma exclusão deste rendimento?')) return;
    setDeletingId(id);
    try {
      await deleteIncome(id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao excluir');
    } finally {
      setDeletingId(null);
    }
  };

  const resetForm = () => {
    setForm({ fii_asset_id: 0, date: '', type: 'dividendo', total_value: 0 });
    setEditingIncome(null);
  };

  const startEdit = (inc: FIIIncomeType) => {
    setEditingIncome(inc);
    setForm({
      fii_asset_id: inc.fii_asset_id,
      date: inc.date.substring(0, 10),
      type: inc.type,
      total_value: inc.total_value,
    });
    setShowForm(true);
  };

  const tickerFor = (assetId: number) =>
    assets.find(a => a.id === assetId)?.ticker ?? `#${assetId}`;

  const grandTotal = incomes.reduce((s, i) => s + i.total_value, 0);

  return (
    <div className="fii-income">
      <div className="page-header">
        <div>
          <h1>💰 Rendimentos FII</h1>
          <p>Dividendos e JCP recebidos</p>
        </div>
        <button className="primary-button" onClick={() => { resetForm(); setShowForm(true); }}>
          + Registrar Rendimento
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {warning && <div className="alert alert-warning">⚠️ {warning}</div>}

      <div className="filters-bar">
        <div className="filter-group">
          <label>Competência</label>
          <select
            value={filterCompetence}
            onChange={e => setFilterCompetence(e.target.value)}
          >
            {competenceOptions.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Ticker</label>
          <select
            value={filterTicker}
            onChange={e => setFilterTicker(e.target.value ? parseInt(e.target.value, 10) : '')}
          >
            <option value="">Todos</option>
            {assets.map(a => (
              <option key={a.id} value={a.id}>{a.ticker}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Tipo</label>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="dividendo">Dividendo</option>
            <option value="jcp">JCP</option>
          </select>
        </div>
      </div>

      {incomes.length > 0 && (
        <div className="summary-card">
          <span className="card-label">Total Acumulado de Rendimentos</span>
          <span className="card-value positive">{formatCurrency(grandTotal)}</span>
        </div>
      )}

      {showForm && (
        <div className="form-container">
          <h3>{editingIncome ? 'Editar Rendimento' : 'Registrar Rendimento'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>FII</label>
                <select
                  value={form.fii_asset_id}
                  onChange={e => setForm(f => ({ ...f, fii_asset_id: parseInt(e.target.value) }))}
                  required
                  disabled={!!editingIncome}
                >
                  <option value={0}>Selecione...</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.ticker}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Tipo</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as 'dividendo' | 'jcp' }))}
                >
                  <option value="dividendo">Dividendo</option>
                  <option value="jcp">JCP</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Data</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Recebido (R$)</label>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={form.total_value}
                  onChange={e => setForm(f => ({ ...f, total_value: parseFloat(e.target.value) || 0 }))}
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>{saving ? 'Salvando...' : (editingIncome ? 'Salvar' : 'Registrar')}</button>
              <button type="button" className="secondary-button" onClick={() => { setShowForm(false); resetForm(); }} disabled={saving}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Carregando rendimentos...</div>
      ) : incomes.length === 0 ? (
        <div className="empty-state">
          <span className="placeholder-icon">💰</span>
          <p>Nenhum rendimento registrado. Clique em <strong>+ Registrar Rendimento</strong> para começar.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="income-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Ticker</th>
                <th>Tipo</th>
                <th>Valor/Cota</th>
                <th>Total</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {incomes.map(inc => (
                <tr key={inc.id}>
                  <td>{formatDate(inc.date.substring(0, 10))}</td>
                  <td><span className="ticker-badge">{tickerFor(inc.fii_asset_id)}</span></td>
                  <td>
                    <span className={`type-badge ${inc.type}`}>
                      {inc.type === 'dividendo' ? 'Dividendo' : 'JCP'}
                    </span>
                  </td>
                  <td className="number-cell">{formatCurrency(inc.value_per_share)}</td>
                  <td className="number-cell positive">{formatCurrency(inc.total_value)}</td>
                  <td className="actions-cell">
                    <div className="actions-wrapper">
                      <button
                        className="action-icon-button"
                        onClick={() => startEdit(inc)}
                        title="Editar"
                      >✏️</button>
                      <button
                        className="action-icon-button danger"
                        onClick={() => handleDelete(inc.id)}
                        title="Excluir"
                        disabled={deletingId === inc.id}
                      >{deletingId === inc.id ? '...' : '🗑️'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FIIIncome;
